<script lang="ts" setup>
import { getDateFormatted, getTimeFormatted12Hours } from '@/services/date'
import { mdiCalendarCheckOutline, mdiClockOutline } from '@mdi/js'
import { IEventEdit } from '@/enitites/event'
import { centsToDollars } from '@/services/currency'

const props = defineProps<{
  eventData: IEventEdit
}>()

const getTiersForDiscount = (tierIds: number[]) => {
  return props.eventData.tiers?.filter((tier) => tierIds.includes(tier.id)) || []
}
</script>

<template>
  <div>
    <div class="view-event">
      <p v-if="!eventData.tiers?.length">
        <span class="view-event__bold-title"> No tiers added yet </span>
      </p>
      <div
        v-for="(tier, index) in eventData.tiers || []"
        :key="index"
        class="view-event__tier"
      >
        <span class="view-event__title">Tier {{ index + 1 }}</span>
        <span><span class="view-event__bold-title"> Title:</span> {{ tier.tierTitle }}</span>
        <span><span class="view-event__bold-title"> Cost:</span> {{ centsToDollars(tier.price) }}</span>
        <span
          ><span class="view-event__bold-title"> Description:</span> {{ tier.description || 'No description' }}</span
        >
      </div>
    </div>
    <v-divider />
    <div class="view-event">
      <p class="view-event__title">Discounts</p>
      <p v-if="!eventData.discounts?.length">
        <span class="view-event__bold-title"> No discounts added yet </span>
      </p>
      <div
        v-for="(discount, index) in eventData.discounts || []"
        :key="index"
        class="view-event__discount"
      >
        <div
          v-if="discount.type === 'code'"
          class="discount-title"
        >
          <span>
            <span class="view-event__bold-title"> Discount Code: </span>
            {{ discount.code }}
          </span>
          <span>
            <span class="view-event__bold-title"> Discount: </span>
            {{ discount.isPercentage ? discount.amount + '%' : '-' + centsToDollars(discount.amount) }}
          </span>
        </div>
        <div
          v-else-if="discount.type === 'early_bird'"
          class="discount-title"
        >
          <span>
            <span class="view-event__bold-title"> Early Bird Pricing: </span>
            {{ discount.isPercentage ? discount.amount + '%' : '-' + centsToDollars(discount.amount) }}
          </span>
        </div>
        <div class="discount-tiers">
          <span
            v-for="tier in getTiersForDiscount(discount.applicableTicketTiers || [])"
            :key="tier.id"
          >
            {{ tier.tierTitle }} ({{ centsToDollars(tier.price) }} -
            {{ discount.isPercentage ? discount.amount + '%' : centsToDollars(discount.amount) }} =
            {{
              discount.isPercentage
                ? '$' + (tier.price - (tier.price * discount.amount) / 100).toFixed(2)
                : centsToDollars(tier.price - discount.amount)
            }})
          </span>
        </div>
        <div class="discount-dates">
          <v-row>
            <v-col
              cols="2"
              class="pt-0"
            >
              <span class="view-event__bold-title">Start:</span>
            </v-col>
            <v-col
              cols="5"
              class="pt-0"
            >
              <span>
                <v-icon>{{ mdiCalendarCheckOutline }}</v-icon>
                {{ getDateFormatted(discount.validFrom).slice(0, 10) }}
              </span>
            </v-col>
            <v-col
              cols="5"
              class="pt-0"
            >
              <span>
                <v-icon>{{ mdiClockOutline }}</v-icon>
                {{ getTimeFormatted12Hours(discount.validFrom) }}
              </span>
            </v-col>
            <v-col
              cols="2"
              class="pt-0"
            >
              <span class="view-event__bold-title">End:</span>
            </v-col>
            <v-col
              cols="5"
              class="pt-0"
            >
              <span>
                <v-icon>{{ mdiCalendarCheckOutline }}</v-icon>
                {{ getDateFormatted(discount.validTo).slice(0, 10) }}
              </span>
            </v-col>
            <v-col
              cols="5"
              class="pt-0"
            >
              <span>
                <v-icon>{{ mdiClockOutline }}</v-icon>
                {{ getTimeFormatted12Hours(discount.validTo) }}
              </span>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.view-event {
  max-width: 400px;
  margin: 0 auto;

  &__title {
    margin: 16px 0;
    color: var(--color-dark-800);
    font-weight: var(--font-weight-semi-bold);
  }

  &__bold-title {
    font-weight: var(--font-weight-semi-bold);
  }

  &__tier {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
  }

  &__discount {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-bottom: 24px;
    padding-top: 12px;

    .discount-tiers {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-bottom: 16px;
    }

    .discount-dates {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
  }
}
</style>
